import { createContext, useState, useEffect, useRef } from "react";
import { useAuth } from "./auth";
import { getThemeCssIndex } from "../utilities/themeMapping";
import { isTrialActive, getTrialExpiresAt, getTrialTimeRemaining, isThemeInTrial } from "../utilities/trialConfig";
import toastService from "../services/toastService";

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8080';

export const themeContext = createContext("red");

export const ThemeProvider = ({ children }) => {
  const [themeName, setThemeName] = useState("Blaze");
  const [ownedThemeIds, setOwnedThemeIds] = useState([1]);
  const [ownedThemeNames, setOwnedThemeNames] = useState(["Blaze"]);
  const [loading, setLoading] = useState(true);
  const [userCreatedAt, setUserCreatedAt] = useState(null);
  const { user, isAuthenticated, themeData: preloadedThemeData, itemsData: preloadedItemsData } = useAuth();
  const trialTimerRef = useRef(null);

  // Hardcoded themes - matches database IDs 1-6
  const HARDCODED_THEMES = [
    { id: 1, name: "Blaze", type: "THEME", price: 0, currency: "USD" },
    { id: 2, name: "Pulse", type: "THEME", price: 5.0, currency: "USD" },
    { id: 3, name: "Aura", type: "THEME", price: 5.0, currency: "USD" },
    { id: 4, name: "Nova", type: "THEME", price: 5.0, currency: "USD" },
    { id: 5, name: "Frost", type: "THEME", price: 5.0, currency: "USD" },
    { id: 6, name: "Spectrum", type: "THEME", price: 5.0, currency: "USD" }
  ];

  // Get CSS theme index for current theme (e.g., "Blaze" -> 0 -> "theme-0")
  const theme = `theme-${getThemeCssIndex(themeName) ?? 0}`;

  // Derived trial state
  const trialActive = isAuthenticated && isTrialActive(userCreatedAt);
  const trialExpiresAt = userCreatedAt ? getTrialExpiresAt(userCreatedAt) : null;

  // Sync theme with toast service whenever it changes
  useEffect(() => {
    toastService.setTheme(theme);
  }, [theme]);

  // Load user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user && preloadedThemeData) {
      // Extract owned themes from /api/user/items
      const ownedIds = preloadedItemsData
        ?.filter(item => item.type === 'THEME' && item.isOwned)
        .map(item => item.id) || [];
      const ownedNames = preloadedItemsData
        ?.filter(item => item.type === 'THEME' && item.isOwned)
        .map(item => item.name) || [];

      setOwnedThemeIds(ownedIds);
      setOwnedThemeNames(ownedNames);

      // Extract trialStart for trial calculation
      if (preloadedThemeData.user.trialStart) {
        setUserCreatedAt(preloadedThemeData.user.trialStart);
      }

      // Set selected theme (already validated by backend)
      if (preloadedThemeData.user.selectedTheme) {
        setThemeName(preloadedThemeData.user.selectedTheme.name);
      }

      setLoading(false);
    } else if (isAuthenticated && user && !preloadedThemeData) {
      // Logged in but /me failed — fall back to defaults
      setOwnedThemeIds([1]);
      setOwnedThemeNames(["Blaze"]);
      setThemeName("Blaze");
      setUserCreatedAt(null);
      setLoading(false);
    } else if (!isAuthenticated) {
      // Reset to default state when not authenticated
      setOwnedThemeIds([1]);
      setOwnedThemeNames(["Blaze"]);
      setThemeName("Blaze");
      setUserCreatedAt(null);
      setLoading(false);
    }
  }, [isAuthenticated, user, preloadedThemeData, preloadedItemsData]);


  const changeTheme = async (newThemeName) => {
    // Check if theme exists in hardcoded list
    const themeData = HARDCODED_THEMES.find((t) => t.name === newThemeName);
    if (!themeData) {
      toastService.show("Theme not found", null, "error");
      return;
    }

    // Check ownership: free OR owned by name OR (trial active AND in trial list)
    const isFree = themeData.price === 0;
    const isOwned = ownedThemeNames.includes(themeData.name);
    const hasTrialAccess = trialActive && isThemeInTrial(themeData.name);

    if (!isFree && !isOwned && !hasTrialAccess && isAuthenticated) {
      toastService.show("Theme not owned", "Please purchase this theme first.", "error");
      return;
    }

    // Update local state immediately for better UX
    const previousTheme = themeName;
    setThemeName(newThemeName);

    // Save to server if authenticated
    if (isAuthenticated && user) {
      try {
        await saveThemeToServer(newThemeName);
      } catch (error) {
        // Server rejected — revert to previous theme
        setThemeName(previousTheme);
        toastService.show("Failed to apply theme", "Server rejected the change.", "error");
        return false;
      }
    }
    return true;
  };

  const saveThemeToServer = async (name) => {
    // POST /api/user/theme - Update user's selected theme
    const response = await fetch(`${API_BASE_URL}/api/user/theme`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ themeName: name })
    });

    if (!response.ok) {
      throw new Error("Server rejected theme update");
    }

    return response.json();
  };

  const refreshThemeData = async () => {
    // No-op: Data refreshed by Store component after purchases
    // LoaderPage handles initial data fetch
  };

  // Function to update owned themes after purchase
  const updateOwnedThemes = (newThemeId) => {
    setOwnedThemeIds(prev => [...prev, newThemeId]);
  };

  // Function to sync owned themes from items array (used by Store)
  const syncOwnedThemesFromItems = (items) => {
    const ownedIds = items
      ?.filter(item => item.type === 'THEME' && (item.isOwned || item.price === 0))
      .map(item => item.id) || [];
    const ownedNames = items
      ?.filter(item => item.type === 'THEME' && (item.isOwned || item.price === 0))
      .map(item => item.name) || [];
    setOwnedThemeIds(ownedIds);
    setOwnedThemeNames(ownedNames);
  };

  // No validation needed on load - backend already validates in /api/user/me
  // Validation only happens when trial timer expires (see below)

  // Trial expiry timer — reset theme when trial ends
  useEffect(() => {
    if (trialTimerRef.current) {
      clearTimeout(trialTimerRef.current);
      trialTimerRef.current = null;
    }

    if (!trialActive || !userCreatedAt) return;

    const remaining = getTrialTimeRemaining(userCreatedAt);
    if (remaining <= 0) return;

    trialTimerRef.current = setTimeout(() => {
      // Trial just expired — check if current theme was trial-only
      const themeData = HARDCODED_THEMES.find(t => t.name === themeName);
      if (themeData && themeData.price > 0 && !ownedThemeNames.includes(themeName) && isThemeInTrial(themeName)) {
        setThemeName("Blaze");
        toastService.show("Trial ended", "Your free trial has expired. Theme reset to Blaze.", "info");
      }
    }, remaining);

    return () => {
      if (trialTimerRef.current) {
        clearTimeout(trialTimerRef.current);
      }
    };
  }, [trialActive, userCreatedAt, themeName, ownedThemeNames]);

  // Add ownership status, trial flags, and CSS mapping to hardcoded themes
  const themesWithOwnership = HARDCODED_THEMES.map(t => {
    const cssIndex = getThemeCssIndex(t.name);
    const isFree = t.price === 0;
    const isOwned = isFree || ownedThemeNames.includes(t.name);
    const isTrialItem = isThemeInTrial(t.name);
    const isTrialAccessible = trialActive && isTrialItem;

    return {
      ...t,
      cssIndex,
      cssClass: cssIndex !== null ? `theme-${cssIndex}` : 'theme-0',
      isOwned,
      isPaid: t.price > 0,
      isTrialItem,
      isTrialAccessible,
      isAccessible: isOwned || isTrialAccessible,
    };
  });

  const contextValue = {
    // Current theme (for CSS classes like "theme-0")
    theme,

    // Current theme name (from DB like "Blaze")
    themeName,

    // Function to change theme by name
    setTheme: changeTheme,

    // Theme data
    ownedThemeIds,
    ownedThemeNames,
    allThemes: themesWithOwnership,
    loading,

    // Trial state
    trialActive,
    trialExpiresAt,
    userCreatedAt,

    // Actions
    refreshThemeData,
    updateOwnedThemes,
    syncOwnedThemesFromItems,

    // Authentication state
    isAuthenticated,
    user,
  };

  return (
    <themeContext.Provider value={contextValue}>
      {children}
    </themeContext.Provider>
  );
};
